import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useDecisionEmbed } from '../../context/DecisionEmbedContext';

const DECISION_PATH = '/resources/entscheidungs-assistent';

function DecisionSidebarPanel({ dashboardPath = '/', guestMode = false }) {
  const location = useLocation();
  const ctx = useDecisionEmbed();
  const [stage, setStage] = useState('welcome');

  useEffect(() => {
    if (location.pathname !== DECISION_PATH || !ctx) return undefined;

    const onMsg = (event) => {
      if (event.origin !== window.location.origin) return;
      const win = ctx.iframeRef?.current?.contentWindow;
      if (!win || event.source !== win) return;
      const data = event.data;
      if (data?.type === 'dm-state') {
        setStage(data.stage || 'welcome');
      }
    };

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [ctx, location.pathname]);

  useEffect(() => {
    if (location.pathname === DECISION_PATH && ctx?.embedReady) {
      ctx.postToEmbed?.({ type: 'dm-host-request-state' });
    }
  }, [ctx, location.pathname, ctx?.embedReady]);

  const steps = useMemo(() => {
    if (stage === 'result') {
      return [
        { label: 'Situation beschrieben', done: true, active: false },
        { label: 'Analyse abgeschlossen', done: true, active: false },
        { label: 'Ergebnis ansehen', done: false, active: true },
      ];
    }
    if (stage === 'input') {
      return [
        { label: 'Gestartet', done: true, active: false },
        { label: 'Situation beschreiben', done: false, active: true },
        { label: 'Ergebnis erhalten', done: false, active: false },
      ];
    }
    return [
      { label: 'Willkommen', done: false, active: true },
      { label: 'Situation beschreiben', done: false, active: false },
      { label: 'Ergebnis erhalten', done: false, active: false },
    ];
  }, [stage]);

  const postToEmbed = ctx?.postToEmbed;

  const handleBackInAssistant = () => {
    if (stage === 'result') {
      postToEmbed?.({ type: 'dm-host-back-to-input' });
      return;
    }
    if (stage === 'input') {
      postToEmbed?.({ type: 'dm-host-back-to-welcome' });
    }
  };

  if (location.pathname !== DECISION_PATH || !ctx) return null;

  const backLinkClass =
    'flex items-center py-2.5 pr-3 text-sm font-medium rounded-lg transition-all duration-150 border-l-2 border-transparent pl-2.5 text-slate-400 hover:bg-white/10 hover:text-slate-200';

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      <div className="shrink-0">
        {stage === 'welcome' ? (
          <Link to={dashboardPath} className={backLinkClass}>
            <ArrowLeft className="h-4 w-4 mr-3 shrink-0" aria-hidden />
            <span>{guestMode ? 'Anmelden für Portal' : 'Zurück zum Portal'}</span>
          </Link>
        ) : (
          <button type="button" onClick={handleBackInAssistant} className={`${backLinkClass} w-full text-left`}>
            <ArrowLeft className="h-4 w-4 mr-3 shrink-0" aria-hidden />
            <span>{stage === 'result' ? 'Zurück zur Eingabe' : 'Zurück zum Start'}</span>
          </button>
        )}

        <div className="mx-3 h-px bg-white/10 my-3" aria-hidden />

        <div className="px-3 pb-3">
          <h2 className="text-[1.05rem] font-bold text-white leading-snug tracking-tight">
            HR-Kompass
          </h2>
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
            Treffe deine passende HR-Entscheidung in wenigen Minuten.
          </p>
        </div>

        <ol className="px-3 pb-1 list-none m-0">
          {steps.map((step, idx) => {
            const isLast = idx === steps.length - 1;
            return (
              <li key={step.label} className="relative flex items-start gap-3.5 py-3 first:pt-0 last:pb-0">
                {!isLast && (
                  <span
                    className={[
                      'absolute left-[11px] top-9 bottom-0 w-[1.5px]',
                      step.done ? 'bg-hrf-coral/40' : 'bg-white/12',
                    ].join(' ')}
                    aria-hidden
                  />
                )}
                <span
                  className={[
                    'relative z-[1] h-6 w-6 shrink-0 rounded-full border-[1.5px] text-[11px] font-bold flex items-center justify-center transition-all duration-200',
                    step.active
                      ? 'bg-hrf-coral border-hrf-coral text-white shadow-[0_0_0_4px_rgba(252,78,72,0.15)]'
                      : step.done
                        ? 'border-hrf-coral/50 text-hrf-coral bg-hrf-coral/10'
                        : 'border-white/20 text-white/35 bg-transparent',
                  ].join(' ')}
                >
                  {step.done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : idx + 1}
                </span>
                <span
                  className={[
                    'pt-0.5 text-[0.82rem] leading-snug',
                    step.active
                      ? 'text-white font-semibold'
                      : step.done
                        ? 'text-white/65 font-medium'
                        : 'text-white/40 font-medium',
                  ].join(' ')}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-auto px-3 pt-4 pb-2 border-t border-white/10">
        <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Account Manager Team</p>
        <div className="flex items-center gap-1.5 mb-2.5">
          {['LM', 'TS', 'AK', 'NB'].map((initials) => (
            <span
              key={initials}
              className="h-7 w-7 rounded-full bg-white/10 border border-white/15 text-[10px] text-white/80 flex items-center justify-center"
            >
              {initials}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DecisionSidebarPanel;
